import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";

import { CrossSellingProduct } from "@hermes/api-model-product";
import { LOCALE } from "@hermes/app-core";
import { CROSS_SELL_PRODUCT, MediaType } from "@hermes/fragments/image-resizer";
import { Locale } from "@hermes/locale";

import { ANGULAR_SPA_ACTIVATION_ON_PERSO_GENERIC_CONFIGURATOR } from "@hermes/states/flipper";
import { getGridTitle } from "@hermes/states/grid-filters-state";
import {
    ProductCategory,
    PRODUCT_LEATHER_CATEGORY,
} from "@hermes/states/personalization-page";
import {
    LazyLoadedProduct,
    ProductPageFacade,
    ProductContentProductClickEvent,
} from "@hermes/states/product-page";
import { AnalyticsService } from "@hermes/utils/analytics";
import { RouterService } from "@hermes/utils-generic/services/router";

import { GridTitle } from "../product-page-grid/product-page-grid.component";

@Component({
    selector: "h-product-page-grid-item",
    templateUrl: "./product-page-grid-item.component.html",
    styleUrls: ["./product-page-grid-item.component.scss"],
})
export class ProductPageGridItemComponent {
    @Input()
    public list: GridTitle | undefined;

    @Input()
    public index: number | undefined;

    @Output()
    public imageInViewPort: EventEmitter<LazyLoadedProduct> =
        new EventEmitter<LazyLoadedProduct>();

    public productInGrid: MediaType = CROSS_SELL_PRODUCT;

    public title!: string;

    private prCrossSellingProduct!: CrossSellingProduct;

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private analyticsService: AnalyticsService,
        private routerService: RouterService,
        private productPageFacade: ProductPageFacade,
    ) {}

    public get crossSellingProduct() {
        return this.prCrossSellingProduct;
    }

    @Input()
    public set crossSellingProduct(crossSellingProduct: CrossSellingProduct) {
        this.prCrossSellingProduct = crossSellingProduct;
        this.title = getGridTitle(crossSellingProduct);
    }

    /**
     * @returns the cross sell product link to go to the product page
     */
    public getProductUrl(): string {
        // all cross sell product item url must end end with a '/'
        const end =
            this.crossSellingProduct.url &&
            !this.crossSellingProduct.url.endsWith("/")
                ? "/"
                : "";
        // we want the full url with base href (/xx/yy/product/*) all the time.
        const localePrefix = this.locale.urlPrefix;
        return `${localePrefix}${this.crossSellingProduct.url}${end}`;
    }

    public onClick(event: MouseEvent | KeyboardEvent): void {
        // stop link event
        event.preventDefault();

        this.analyticsService.sendData(
            new ProductContentProductClickEvent({
                product: this.crossSellingProduct,
                list: this.list,
            }),
        );

        this.navigate();
    }

    public navigate(): void {
        const isLeatherPersoProduct: boolean =
            PRODUCT_LEATHER_CATEGORY.includes(
                this.crossSellingProduct.persoProductType as ProductCategory,
            );

        if (isLeatherPersoProduct) {
            this.routerService.navigate(
                this.getProductUrl(),
                ANGULAR_SPA_ACTIVATION_ON_PERSO_GENERIC_CONFIGURATOR,
            );
        }
        this.productPageFacade.defaultImageGalleryPosition();
        this.routerService.spaRedirect(this.getProductUrl());
    }

    public handleImageInViewPort(status: boolean): void {
        if (status && this.index !== undefined) {
            this.imageInViewPort.emit({
                index: this.index,
                product: this.crossSellingProduct,
            });
        }
    }

    /**
     * A11Y need no space in aria-label and id.
     */
    public htmlIdFromSku(): string {
        return (
            `${this.list}`
                .replace(/_/g, "-")
                // eslint-disable-next-line unicorn/prefer-spread
                .concat("-", this.crossSellingProduct.sku.replace(/ /g, ""))
        );
    }
}
